/*eslint max-lines-per-function: ["error", 300]*/
/* eslint-disable complexity */
import React, { useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Card from 'react-bootstrap/Card';
import { Alert, Form } from 'react-bootstrap';
import * as Icon from 'react-bootstrap-icons';

import SpinnerButton from '../../../common/components/Button';
import ValidationAlert from '../../../app/components/ValidationAlert';
import FormRow from '../../../app/components/FormRow';
import {
  CreateUpdateVisitRegistrationDto,
  GetExamDto,
  GetExamTypeDto,
  GetPaymentDto,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import LastEditorInfo from '../../../app/components/LastEditorInfo';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import momencik from '../../../common/momencik';
import ConfirmationButton from '../../../app/components/ConfirmationButton';
import ExamTypeSelector from '../Selects/ExamTypeSelector';
import AppointmentChannelSelector from '../Selects/AppointmentChannelSelector';
import { useNavigation } from '../../../common/navigation';

import { VisitPaymentStatus } from './VisitPaymentStatus';

interface VisitRegistrationEditorProps {
  id?: string;
  visitRegistration?: CreateUpdateVisitRegistrationDto;
  exam?: GetExamDto;
  payments?: GetPaymentDto[];
  examType?: GetExamTypeDto;
  sellDocumentType?: string | null;
  creatorName?: string;
  onCancel?: () => void;
  show?: boolean;
  title?: string;
  onSubmit?: (
    visit: CreateUpdateVisitRegistrationDto,
    id?: string,
  ) => Promise<{
    saved: boolean;
    errors: string[];
  }>;
  submitText?: string;
  onDelete?: (id: string) => void;
  askQuestion?: string;
  addVisit?: boolean;
}

export const VisitRegistrationEditor: React.FC<
  VisitRegistrationEditorProps
> = props => {
  const [visit, setVisit] = useState(props.visitRegistration);
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const spd = useSpdCore();
  const nav = useNavigation();

  useEffect(() => {
    if (props.show) {
      setSaveErrors([]);
      setShowSaveErrors(false);
      setVisit(props.visitRegistration);
    } else {
      setVisit({});
      setSaveErrors([]);
      setShowSaveErrors(false);
    }
  }, [props.show, props.visitRegistration, props.id]);

  const changeAppointmentChannel = (channelId: string) => {
    if (channelId) {
      spd.appointmentChannels
        .get(channelId)
        .then(channel => {
          if (channel) {
            setVisit({
              ...visit,
              appointmentChannelId: channelId,
              visitTime: Number(channel.timeWindow) || 20,
            });
          }
        })
        .catch(errorResponse => {
          console.log(errorResponse);
        });
    } else {
      setVisit({ ...visit, appointmentChannelId: channelId });
    }
  };

  const onSubmit = async () => {
    if (props.onSubmit && visit) {
      setIsSaving(true);
      props
        .onSubmit(visit, props.id)
        .then(saveStatus => {
          if (!saveStatus.saved) {
            setSaveErrors(saveStatus.errors);
            setShowSaveErrors(true);
            return;
          }
        })
        .catch(error => {
          console.error('Error during add visit registration:', error);
        })
        .finally(() => {
          setIsSaving(false);
        });
    }
  };

  const onCreateExam = async () => {
    setIsSaving(true);
    try {
      if (props.onSubmit && visit) {
        await props.onSubmit(visit, props.id);
      }
      if (props.id) {
        nav.testGivingWithRegistration(props.id);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const propertyChange = (obj: Partial<CreateUpdateVisitRegistrationDto>) => {
    setVisit({ ...visit, ...obj });
  };

  return (
    <>
      <Modal
        onHide={props.onCancel}
        show={props.show}
        centered={true}
        keyboard={true}
        backdrop="static"
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>{props.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Card className="mb-2">
            <Card.Header>Rejestracja wizyty</Card.Header>
            <Card.Body>
              <Form className="d-grid gap-3">
                <VisitPaymentStatus
                  payments={props.payments}
                  examType={props.examType}
                />
                <Alert show={visit?.active === false} variant="warning">
                  Wizyta usunięta
                </Alert>
                {props.id && props.creatorName && (
                  <FormRow controlId="userName" label="Utworzył">
                    <Form.Control value={props.creatorName} disabled />
                  </FormRow>
                )}
                <FormRow controlId="firstname" label="Imię">
                  <Form.Control
                    disabled={visit?.active === false}
                    value={visit?.firstname || ''}
                    onChange={e =>
                      propertyChange({ firstname: e.target.value })
                    }
                  />
                </FormRow>
                <FormRow controlId="surname" label="Nazwisko">
                  <Form.Control
                    disabled={visit?.active === false}
                    value={visit?.surname || ''}
                    onChange={e => propertyChange({ surname: e.target.value })}
                  />
                </FormRow>
                <FormRow controlId="mail" label="E-mail">
                  <Form.Control
                    disabled={visit?.active === false}
                    value={visit?.mail || ''}
                    onChange={e => propertyChange({ mail: e.target.value })}
                  />
                </FormRow>
                <FormRow controlId="phone" label="Telefon">
                  <Form.Control
                    disabled={visit?.active === false}
                    value={visit?.phone || ''}
                    onChange={e => propertyChange({ phone: e.target.value })}
                  />
                </FormRow>
                <FormRow controlId="examType" label="Typ badania">
                  <ExamTypeSelector
                    readOnly={visit?.active === false}
                    value={visit?.examTypeId || ''}
                    onChange={e => propertyChange({ examTypeId: e.value })}
                  />
                </FormRow>
                {!props.visitRegistration?.online && (
                  <FormRow
                    controlId="appointmentChannel"
                    label="Kanał rejestracji wizyty"
                  >
                    <AppointmentChannelSelector
                      readOnly={visit?.active === false}
                      value={visit?.appointmentChannelId || ''}
                      onChange={e => changeAppointmentChannel(e.value)}
                    />
                  </FormRow>
                )}
                <FormRow controlId="description" label="Opis">
                  <Form.Control
                    disabled={visit?.active === false}
                    as="textarea"
                    rows={2}
                    name="description"
                    value={visit?.description || ''}
                    onChange={e =>
                      propertyChange({ description: e.target.value })
                    }
                  />
                </FormRow>
                {props.addVisit && !props.visitRegistration?.online && (
                  <>
                    <FormRow controlId="startVisit" label="Początek wizyty">
                      <Form.Control
                        disabled={visit?.active === false}
                        type="datetime-local"
                        name="datepic"
                        required={false}
                        placeholder="DateRange"
                        value={momencik(
                          visit?.visitDateTime,
                          'YYYY-MM-DDTHH:mm',
                        )}
                        onChange={e =>
                          propertyChange({
                            visitDateTime: new Date(e.target.value),
                          })
                        }
                      />
                    </FormRow>
                    <FormRow
                      controlId="visitTime"
                      label="Okno czasowe wizyty (min)"
                    >
                      <Form.Control
                        disabled={visit?.active === false}
                        type="number"
                        value={visit?.visitTime || 0}
                        onChange={e =>
                          propertyChange({ visitTime: Number(e.target.value) })
                        }
                      />
                    </FormRow>
                    {props.exam && (
                      <FormRow controlId="exam" label="Utworzone badanie">
                        <div className="d-flex align-items-center gap-2">
                          <Form.Control
                            value={props.exam?.number || ''}
                            disabled={true}
                          />
                          <Button
                            variant="outline-secondary"
                            title="Otwórz badanie w nowym oknie"
                            onClick={() =>
                              props.exam?.id &&
                              nav.examTests(props.exam.id, true)
                            }
                          >
                            <Icon.BoxArrowUpRight />
                          </Button>
                        </div>
                      </FormRow>
                    )}
                  </>
                )}
              </Form>
            </Card.Body>
          </Card>
          {props.askQuestion && (
            <ValidationAlert
              show={true}
              errors={[props.askQuestion]}
              className="mt-3"
              variant="info"
            />
          )}
          <ValidationAlert
            show={showSaveErrors}
            errors={saveErrors}
            className="mt-3"
          />
        </Modal.Body>
        <Modal.Footer>
          {props.id && (
            <LastEditorInfo id={props.id} method={spd.visits.getLastEditor} />
          )}
          {props.onCancel && (
            <Button variant="outline-secondary" onClick={props.onCancel}>
              Zamknij
            </Button>
          )}
          {props.onDelete && props.id && (
            <ConfirmationButton
              className="mx-1"
              confirmation="Czy na pewno usunąć rezerwację wizyty?"
              warnings={
                props.exam
                  ? [
                      'Uwaga: Na podstawie tej wizyty zostało wygenerowane badanie.',
                    ]
                  : undefined
              }
              variant="outline-danger"
              onOK={() =>
                props.onDelete && props.id && props.onDelete(props.id)
              }
            >
              Usuń
            </ConfirmationButton>
          )}
          {props.onSubmit && (
            <SpinnerButton
              disabled={visit?.active === false}
              isLoading={isSaving}
              variant="outline-primary"
              onClick={onSubmit}
            >
              {props.submitText || 'Zapisz'}
            </SpinnerButton>
          )}
          {props.id && props.onSubmit && !visit?.examId && (
            <SpinnerButton
              disabled={visit?.active === false}
              isLoading={isSaving}
              variant="outline-primary"
              onClick={onCreateExam}
            >
              Utwórz badanie
            </SpinnerButton>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};
